import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useSelector } from 'react-redux';
import { adminAPI } from '../services/api';
import { RootState } from '../store/store';

interface SystemSettings {
  siteName: string;
  siteDescription: string;
  maintenanceMode: boolean;
  allowRegistration: boolean;
  requireEmailVerification: boolean;
  supportedFormats: string[];
  elasticsearchTimeout: number;
  enableNotifications: boolean;
  logLevel: string;
  sessionTimeout: number;
  // Footer settings
  footerBrandName: string;
  footerDescription: string;
  footerContactEmail: string;
  footerContactPhone: string;
  footerContactAddress: string;
  footerProductLinks: string; // JSON string
  footerResourceLinks: string; // JSON string
  footerCompanyLinks: string; // JSON string
  footerSocialLinks: string; // JSON string
  footerCopyrightText: string;
}

interface SystemSettingsContextType {
  settings: SystemSettings | null;
  loading: boolean;
  error: string | null;
  refreshSettings: () => Promise<void>;
}

const SystemSettingsContext = createContext<SystemSettingsContextType | undefined>(undefined);

interface SystemSettingsProviderProps {
  children: ReactNode;
}

export const SystemSettingsProvider: React.FC<SystemSettingsProviderProps> = ({ children }) => {
  const [settings, setSettings] = useState<SystemSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const { user } = useSelector((state: RootState) => state.auth);

  const loadSettings = async (isRefresh = false) => {
    // Prevent multiple concurrent requests
    if (isRefresh && isRefreshing) {
      return;
    }
    
    try {
      if (isRefresh) {
        setIsRefreshing(true);
      } else {
        setLoading(true);
      }
      setError(null);
      
      // Check if user is authenticated, if not use public settings directly
      const token = localStorage.getItem('token');
      let response;
      
      if (token && user?.role === 'admin') {
        // Admin user, fetch the full admin settings
        try {
          response = await adminAPI.getSystemSettings();
        } catch (err: any) {
          // Si 401 (Unauthorized) ou 403 (Forbidden), utiliser les paramètres publics
          if (err.response?.status === 401 || err.response?.status === 403) {
            // Token invalide ou utilisateur non-admin, utiliser les paramètres publics
            console.log('[SystemSettings] Utilisateur non-admin ou token invalide, utilisation des paramètres publics');
            response = await adminAPI.getPublicSystemSettings();
          } else {
            // Autre erreur, réessayer avec les paramètres publics
            console.warn('[SystemSettings] Erreur lors du chargement des paramètres admin, utilisation des paramètres publics:', err.response?.status);
            try {
              response = await adminAPI.getPublicSystemSettings();
            } catch (publicErr: any) {
              // Si même les paramètres publics échouent, utiliser les valeurs par défaut
              console.error('[SystemSettings] Impossible de charger les paramètres publics, utilisation des valeurs par défaut');
              setSettings(defaultSystemSettings);
              setError(null); // Ne pas afficher d'erreur si on a des valeurs par défaut
              return;
            }
          }
        }
      } else {
        // User is not authenticated, use public settings directly
        response = await adminAPI.getPublicSystemSettings();
      }
      if (response.data.success) {
        setSettings(response.data.settings);
      } else {
        setError('Failed to load system settings');
      }
    } catch (err: any) {
      console.error('Error loading system settings:', err);
      
      // Si on a déjà des paramètres par défaut, ne pas afficher d'erreur
      if (settings === defaultSystemSettings) {
        setError(null);
        return;
      }
      
      // Si c'est un 403 et qu'on n'a pas encore de paramètres, utiliser les valeurs par défaut
      if (err.response?.status === 403 && !settings) {
        console.log('[SystemSettings] 403 Forbidden, utilisation des paramètres par défaut');
        setSettings(defaultSystemSettings);
        setError(null);
        return;
      }
      
      // Provide more specific error messages
      if (err.code === 'ECONNREFUSED' || err.message.includes('Network Error')) {
        setError('Backend server is not running. Please start the backend server on port 5010.');
        // Utiliser les paramètres par défaut si le serveur n'est pas disponible
        if (!settings) {
          setSettings(defaultSystemSettings);
        }
      } else if (err.response?.status === 500) {
        setError('Backend server error. Please check the server logs.');
        // Utiliser les paramètres par défaut en cas d'erreur serveur
        if (!settings) {
          setSettings(defaultSystemSettings);
        }
      } else if (err.response?.status === 403) {
        // 403 Forbidden - utilisateur non-admin, utiliser les paramètres par défaut
        console.log('[SystemSettings] 403 Forbidden, utilisation des paramètres par défaut');
        setSettings(defaultSystemSettings);
        setError(null);
      } else {
        setError('Failed to load system settings');
        // Utiliser les paramètres par défaut en dernier recours
        if (!settings) {
          setSettings(defaultSystemSettings);
        }
      }
      
      // Don't clear settings on error, keep the last known good settings
    } finally {
      if (isRefresh) {
        setIsRefreshing(false);
      } else {
        setLoading(false);
      }
    }
  };

  const refreshSettings = async () => {
    await loadSettings(true);
  };

  useEffect(() => {
    loadSettings();
  }, [user?.role]);

  const value: SystemSettingsContextType = {
    settings,
    loading,
    error,
    refreshSettings
  };

  return (
    <SystemSettingsContext.Provider value={value}>
      {children}
    </SystemSettingsContext.Provider>
  );
};

export const useSystemSettings = (): SystemSettingsContextType => {
  const context = useContext(SystemSettingsContext);
  if (context === undefined) {
    throw new Error('useSystemSettings must be used within a SystemSettingsProvider');
  }
  return context;
};

// Default settings fallback
export const defaultSystemSettings: SystemSettings = {
  siteName: 'SupplyLink Analytics Hub',
  siteDescription: 'Agricultural data analytics platform',
  maintenanceMode: false,
  allowRegistration: true,
  requireEmailVerification: false,
  supportedFormats: ['json', 'csv', 'xlsx'],
  elasticsearchTimeout: 30000,
  enableNotifications: true,
  logLevel: 'info',
  sessionTimeout: 3600,
  // Footer settings
  footerBrandName: 'E-LEAVING LAB',
  footerDescription: 'The all-in-one platform for modern agricultural teams.',
  footerContactEmail: 'contact@eleavinglab.com',
  footerContactPhone: '+33 1 23 45 67 89',
  footerContactAddress: 'Paris, France',
  footerProductLinks: JSON.stringify([
    { name: 'Analytics', href: '/analytics' },
    { name: 'Businesses', href: '/businesses' },
    { name: 'Courses', href: '/courses' },
    { name: 'Events', href: '/events' },
    { name: 'News', href: '/news' },
    { name: 'Projects', href: '/projects' },
    { name: 'Contact Us', href: '/contact' }
  ]),
  footerResourceLinks: JSON.stringify([
    { name: 'Documentation', href: '#' },
    { name: 'API Reference', href: '#' },
    { name: 'Guides', href: '#' },
    { name: 'Blog', href: '#' },
    { name: 'Support', href: '#' }
  ]),
  footerCompanyLinks: JSON.stringify([
    { name: 'About', href: '#' },
    { name: 'Careers', href: '#' },
    { name: 'Partners', href: '#' },
    { name: 'Press', href: '#' },
    { name: 'Contact', href: '#' }
  ]),
  footerSocialLinks: JSON.stringify([
    { platform: 'linkedin', url: '#' },
    { platform: 'twitter', url: '#' },
    { platform: 'facebook', url: '#' },
    { platform: 'instagram', url: '#' }
  ]),
  footerCopyrightText: '© 2025 E-LEAVING LAB. All rights reserved.'
};
